import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Manager context & components
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Buildings from './components/Buildings';
import ContractorList from './components/ContractorList';
import WorkOrders from './components/WorkOrders';
import DocumentVault from './components/DocumentVault';
import VoiceEmail from './components/VoiceEmail';
import Meetings from './components/Meetings';
import Invoices from './components/Invoices';
import Compliance from './components/Compliance';
import Login from './components/Login';
import Units from './components/Units';
import EmailHub from './components/EmailHub';
import PredictiveMaintenance from './components/PredictiveMaintenance';
import Admin from './components/Admin';
import BlockedScreen from './components/BlockedScreen';
import Subscribe from './components/Subscribe';
import DataImport from './components/DataImport';
import Arrears from './components/Arrears';
import BulkMessage from './components/BulkMessage';
import FinancialReports from './components/FinancialReports';
import ChartOfAccounts from './components/ChartOfAccounts';
import CertificateGenerator from './components/CertificateGenerator';
import DocumentRequests from './components/DocumentRequests';

// Public document-request portal (no auth)
import DocumentRequestPortal from './portal/DocumentRequestPortal';
import RequestStatus from './portal/RequestStatus';

// Owner portal context & components
import { OwnerAuthProvider, useOwnerAuth } from './context/OwnerAuthContext';
import OwnerLogin from './owner/OwnerLogin';
import OwnerLayout from './owner/OwnerLayout';
import OwnerDashboard from './owner/OwnerDashboard';
import OwnerPayments from './owner/OwnerPayments';
import OwnerMaintenance from './owner/OwnerMaintenance';
import OwnerDocuments from './owner/OwnerDocuments';
import OwnerChat from './owner/OwnerChat';

// Council portal context & components
import { CouncilAuthProvider, useCouncilAuth } from './context/CouncilAuthContext';
import CouncilLogin from './council/CouncilLogin';
import CouncilLayout from './council/CouncilLayout';
import CouncilDashboard from './council/CouncilDashboard';
import CouncilMeetings from './council/CouncilMeetings';
import CouncilWorkOrders from './council/CouncilWorkOrders';
import CouncilFinancials from './council/CouncilFinancials';

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );
}

function ManagerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function OwnerRoute({ children }) {
  const { owner, loading } = useOwnerAuth();
  if (loading) return <Spinner />;
  return owner ? children : <Navigate to="/owner/login" replace />;
}

function CouncilRoute({ children }) {
  const { council, loading } = useCouncilAuth();
  if (loading) return <Spinner />;
  return council ? children : <Navigate to="/council/login" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  const { owner } = useOwnerAuth();
  const { council } = useCouncilAuth();

  return (
    <Routes>
      {/* ── Council Portal ────────────────────────────── */}
      <Route
        path="/council/login"
        element={council ? <Navigate to="/council/dashboard" replace /> : <CouncilLogin />}
      />
      <Route path="/council" element={<CouncilRoute><CouncilLayout /></CouncilRoute>}>
        <Route index element={<Navigate to="/council/dashboard" replace />} />
        <Route path="dashboard"  element={<CouncilDashboard />} />
        <Route path="meetings"   element={<CouncilMeetings />} />
        <Route path="workorders" element={<CouncilWorkOrders />} />
        <Route path="financials" element={<CouncilFinancials />} />
      </Route>

      {/* ── Owner Portal ─────────────────────────────── */}
      <Route
        path="/owner/login"
        element={owner ? <Navigate to="/owner/dashboard" replace /> : <OwnerLogin />}
      />
      <Route path="/owner" element={<OwnerRoute><OwnerLayout /></OwnerRoute>}>
        <Route index element={<Navigate to="/owner/dashboard" replace />} />
        <Route path="dashboard"   element={<OwnerDashboard />} />
        <Route path="payments"    element={<OwnerPayments />} />
        <Route path="maintenance" element={<OwnerMaintenance />} />
        <Route path="documents"   element={<OwnerDocuments />} />
        <Route path="chat"        element={<OwnerChat />} />
      </Route>

      {/* ── Manager App ───────────────────────────────── */}
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <Login />}
      />
      {/* Public / semi-public routes */}
      <Route path="/blocked"   element={<BlockedScreen />} />
      <Route path="/subscribe" element={<Subscribe />} />

      {/* Public document-request portal — no auth, no account required */}
      <Route path="/request-documents" element={<DocumentRequestPortal />} />
      <Route path="/request-documents/status/:token" element={<RequestStatus />} />

      <Route path="/" element={<ManagerRoute><Layout /></ManagerRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="buildings"   element={<Buildings />} />
        <Route path="contractors" element={<ContractorList />} />
        <Route path="workorders"  element={<WorkOrders />} />
        <Route path="documents"   element={<DocumentVault />} />
        <Route path="voice-email" element={<VoiceEmail />} />
        <Route path="meetings"    element={<Meetings />} />
        <Route path="invoices"    element={<Invoices />} />
        <Route path="compliance"  element={<Compliance />} />
        <Route path="units"       element={<Units />} />
        <Route path="email-hub"   element={<EmailHub />} />
        <Route path="predictions"   element={<PredictiveMaintenance />} />
        <Route path="financials"    element={<FinancialReports />} />
        <Route path="arrears"            element={<Arrears />} />
        <Route path="bulk-message"       element={<BulkMessage />} />
        <Route path="chart-of-accounts"  element={<ChartOfAccounts />} />
        <Route path="certificates"       element={<CertificateGenerator />} />
        <Route path="document-requests"  element={<DocumentRequests />} />
        <Route path="import"        element={<DataImport />} />
        <Route path="admin"         element={<Admin />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OwnerAuthProvider>
          <CouncilAuthProvider>
            <AppRoutes />
            <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
          </CouncilAuthProvider>
        </OwnerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
